import { ComponentProps, FC } from 'react'
import { create } from 'zustand'

import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { PartialBy } from '@/models'

import {
  OpenPopupOptions,
  PopupEntry,
  PopupProviderStoreType,
  PopupWithRequiredProps,
  RequestCloseWithConfirmationOptions,
} from './create-popup-factory.types'
import { PopupProvider } from './popup-provider'

export const createPopupFactory = () => {
  const usePopupStore = create<PopupProviderStoreType>(() => {
    return {
      isAnimated: false,
      isClosing: false,
      mountedProviders: 0,
      nextId: 0,
      nextPopup: null,
      resolveClose: [],
      stack: [],
    }
  })

  const mountProvider = () => {
    usePopupStore.setState((state) => {
      if (state.mountedProviders > 0) {
        console.warn(
          `You are trying to mount multiple 'PopupProviders' from 'createPopupFactory'.\nThis will lead to problem that you will see multiple identical copies of popups.\nPlease review your code and make sure you are only mounting one 'PopupProvider'`,
        )
      }

      return { ...state, mountedProviders: state.mountedProviders + 1 }
    })

    return () => {
      usePopupStore.setState((state) => {
        return { ...state, mountedProviders: state.mountedProviders - 1 }
      })
    }
  }

  const checkForProvider = () => {
    const hasProvider = usePopupStore.getState().mountedProviders > 0
    if (!hasProvider) {
      console.warn(
        `You are trying to operate with a 'createPopupFactory' without mounting the 'PopupProvider' from it.\nPlease make sure you are mounting the 'PopupProvider' before using 'createPopupFactory'`,
      )
    }
    return hasProvider
  }

  const getActiveEntry = (): PopupEntry | null => {
    const { stack } = usePopupStore.getState()
    return stack.length > 0 ? stack[stack.length - 1] : null
  }

  // TODO: sync with Igor's parking management logic about stacking popups
  const clear = (wholeStack = false) => {
    const { resolveClose, nextPopup, stack } = usePopupStore.getState()
    resolveClose?.forEach((resolve) => resolve())

    // Note: When there are suspended popups below the active one, restore the previous popup
    // instead of fully clearing. Suspended popups stay mounted (preserving state) and are
    // revealed one by one as the popups above them close.
    if (!wholeStack && stack.length > 1) {
      usePopupStore.setState({
        isAnimated: false,
        isClosing: false,
        nextPopup: null,
        resolveClose: [],
        stack: stack.slice(0, -1),
      })
      return
    }

    usePopupStore.setState({
      isAnimated: false,
      isClosing: false,
      nextPopup: null,
      resolveClose: [],
      stack: nextPopup ? [nextPopup] : [],
    })
  }

  const startClosing = () => {
    const { isAnimated } = usePopupStore.getState()

    if (isAnimated) {
      usePopupStore.setState({ isClosing: true })
    } else {
      clear()
    }
  }

  const getCurrentPopup = () => {
    return getActiveEntry()?.component ?? null
  }

  const checkIfOpen = <P extends PopupWithRequiredProps>(
    component: FC<P>,
  ): PartialBy<P, 'onClose'> | null => {
    const active = getActiveEntry()
    if (active?.component === component) {
      return active.props as PartialBy<P, 'onClose'>
    }
    return null
  }

  const close = <P extends PopupWithRequiredProps>(component: FC<P>) => {
    if (!checkForProvider()) return

    const currentActivePopup = getCurrentPopup()
    if (!currentActivePopup) {
      console.warn(
        `You are trying to close '${component.name}' popup when there isn't any one open`,
      )
      return
    }

    if (currentActivePopup !== component) {
      console.warn(`You are trying to close '${component.name}' popup that isn't open`)
      return
    }

    startClosing()
  }

  const closeIf = <P extends PopupWithRequiredProps>(component: FC<P>) => {
    if (!checkForProvider()) return
    if (!checkIfOpen(component)) return
    startClosing()
  }

  const open = <P extends PopupWithRequiredProps>(
    component: FC<P>,
    props: PartialBy<P, 'onClose'>,
    options: OpenPopupOptions = {},
  ) => {
    if (!checkForProvider()) return

    const { force = false, suspendCurrent = false } = options
    const { isClosing } = usePopupStore.getState()
    const activeEntry = getActiveEntry()

    if (isClosing && !force) {
      usePopupStore.setState((state) => ({
        nextId: state.nextId + 1,
        nextPopup: { component, id: state.nextId, props },
      }))
      return
    }

    if (!activeEntry) {
      usePopupStore.setState((state) => ({
        nextId: state.nextId + 1,
        stack: [{ component, id: state.nextId, props }],
      }))
      return
    }

    if (activeEntry.component === component) {
      console.warn(
        `You are trying to open '${component.name}' popup that's already open. If you want to update it, use the '.update(...)' method instead. Otherwise consider '.toggle(...)' method`,
      )
      return
    }

    if (force) {
      // Note: force-open skips animation of the current popup to immediately replace it
      clear()
      usePopupStore.setState((state) => ({
        nextId: state.nextId + 1,
        stack: [{ component, id: state.nextId, props }],
      }))
      return
    }

    if (suspendCurrent) {
      // Note: Push the new popup onto the stack without closing the current one.
      // The current popup stays mounted but hidden so its internal state is preserved.
      // When the new popup closes, clear() pops the stack and the suspended one reappears.
      usePopupStore.setState((state) => ({
        nextId: state.nextId + 1,
        stack: [...state.stack, { component, id: state.nextId, props }],
      }))
      return
    }

    console.warn(
      `You are trying to open '${component.name}' popup whereas '${activeEntry.component.name}' is already open. Consider cleanup the current one before opening a new one or pass 'force:true' option`,
    )
  }

  const update = <P extends PopupWithRequiredProps>(component: FC<P>, props: Partial<P>) => {
    if (!checkForProvider()) return

    const activeEntry = getActiveEntry()
    if (!activeEntry) {
      console.warn(
        `You are trying to update '${component.name}' popup when there isn't any one open`,
      )
      return
    }

    if (activeEntry.component !== component) {
      console.warn(`You are trying to update '${component.name}' popup that isn't open`)
      return
    }

    usePopupStore.setState((state) => {
      const updatedStack = [...state.stack]
      updatedStack[updatedStack.length - 1] = {
        ...updatedStack[updatedStack.length - 1],
        props: { ...updatedStack[updatedStack.length - 1].props, ...props },
      }
      return { stack: updatedStack }
    })
  }

  const toggle = <P extends PopupWithRequiredProps>(
    component: FC<P>,
    props: PartialBy<P, 'onClose'>,
  ) => {
    if (!checkForProvider()) return

    const activeEntry = getActiveEntry()

    if (activeEntry && activeEntry.component !== component) {
      console.warn(
        `You are trying to toggle '${component.name}' popup whereas '${activeEntry.component.name}' is already open. Consider cleanup the current one before opening a new one`,
      )
      return
    }

    if (activeEntry?.component === component) {
      startClosing()
      return
    }

    usePopupStore.setState((state) => ({
      nextId: state.nextId + 1,
      stack: [{ component, id: state.nextId, props }],
    }))
  }

  const requestCloseWithConfirmation = <P extends PopupWithRequiredProps>({
    modalInstance,
    onClose,
    shouldConfirm,
  }: RequestCloseWithConfirmationOptions<P>) => {
    if (!shouldConfirm) {
      onClose()
      return
    }

    open(
      ConfirmationPopup,
      {
        // Note: onConfirm already pops the stack; autoClose would pop one extra popup (the suspended one below)
        autoClose: false,
        children: 'You have unsaved changes. Are you sure you want to close this modal?',
        confirmText: 'Yes, Close',
        onConfirm: () => {
          clear()
          closeIf(modalInstance)
        },
        title: 'Discard unsaved changes?',
        variant: 'warning',
      },
      { suspendCurrent: true },
    )
  }

  return {
    checkIfOpen,
    clear,
    close,
    closeIf,
    getCurrentPopup,
    open,
    PopupProvider: () => (
      <PopupProvider
        usePopupStore={usePopupStore}
        mountProvider={mountProvider}
        startClosing={startClosing}
        clear={clear}
      />
    ),
    requestCloseWithConfirmation,
    toggle,
    update,
  }
}
